import React from 'react';
import { isEmpty } from 'lodash';
import moment from 'moment-timezone';
import Creatives from '../../components/creatives/Creatives';
import Publishers from '../../components/publishers/Publishers';
import SegmentsDetails from '../../components/segments/SegmentsDetails';
import VideoCompletionRate from '../../components/VideoCompletionRate';
import LineItemsCard from './components/lineItems/LineItemsCard';
import CampaignsCard from './components/campaigns/CampaignsCard';
import EntityCardSkeleton from '../../components/EntityCardSkeleton';

const colStyle = {
  cssStyle: {
    backgroundColor: '#fff',
    padding: '10px',
  },
  histogram: { width: 'auto' },
  divider: { height: '6em' },
};

// =============================================================================
// OverviewTab — Display tab's non-chart content.
//
// The KPI bar, date range selector, and chart now live in the unified
// `.campaign-overview__performance` card composed at CampaignsDetailsView.js.
// This component only owns creatives / publishers / segments / videoLogs /
// lineItems / campaigns sections rendered below that card.
// =============================================================================

// Wrapper applying the Display-tab's section spacing (mt:10 + stacking z)
// around the shared `<EntityCardSkeleton/>`. The inline z-index ladder
// matches the non-loading sections below so loading/loaded states swap
// without re-layout.
const CardSkeleton = ({ zIndex = 1 }) => (
  <div style={{ marginTop: 10, position: 'relative', zIndex }}>
    <EntityCardSkeleton />
  </div>
);

const OverviewTab = ({
  chartData,
  chartsLoading,
  selectedCampaign,
  hasInsights,
  byCampaignRangeDate,
  selectedSegmentsDimension,
  setSegmentsDimension,
  userPermission,
  providerIndex,
}) => {
  let videoLogs, creatives, publishers, segments, lineItems, campaigns,
    creativesWithDates, publishersWithDates, chartsBuilt;
  const { segment, isTrafficker } = userPermission;

  try {
    if (chartData) {
      videoLogs =
        chartData.videoLogs &&
        chartData.videoLogs.filter(
          (log) =>
            log.video_firstquartile > 0 &&
            log.video_midpoint > 0 &&
            log.video_thirdquartile > 0 &&
            log.video_complete > 0
        );

      segments = chartData.segments;
      chartsBuilt = chartData.chartsBuilt;
      creatives = chartData.creatives;
      publishers = chartData.publishers;
      lineItems = chartData.lineItems;
      campaigns = chartData.campaigns;

      if (chartData.displayReports && chartData.displayReports.length > 0) {
        // Collect each provider's rows as a CHUNK, then flatten once.
        //
        // This used to be `all.push(...reportData.creativesWithDates)`, which
        // passes every row as a separate function argument — past ~65k rows V8
        // throws `RangeError: Maximum call stack size exceeded`. The throw was
        // swallowed by the catch below, leaving creativesWithDates /
        // publishersWithDates undefined, which silently dropped both tables
        // onto the unfiltered fallback path — i.e. the Display tab's date
        // picker stopped filtering creatives and publishers on any campaign
        // with a large enough report. Spreading CHUNKS (one per provider, a
        // handful at most) instead of ROWS keeps the argument count tiny.
        //
        // Provider scoping (#11679 follow-up): when a specific campaign tab is
        // selected, read ONLY that provider's chunk. `displayReports` passes
        // through DisplayTabController un-sliced, so flattening every provider
        // here left Creatives/Publishers showing all-campaign rows while
        // Segments/Campaigns — which read the already-sliced base arrays —
        // correctly followed the tab. A date range is always seeded
        // (DisplayTabController:266), so this WithDates path is the live one and
        // the sliced base arrays below were effectively unreachable.
        //
        // An unresolvable index falls back to every provider: that is the honest
        // answer when we cannot tell which campaign is meant. A provider that
        // resolves but carries no dated rows leaves these null, which drops to
        // the base `creatives`/`publishers` — already sliced to that provider,
        // just not date-filtered. Never another provider's rows.
        const parsedProvider = parseInt(providerIndex, 10);
        const selectedReport =
          providerIndex !== 'All' && !Number.isNaN(parsedProvider)
            ? chartData.displayReports[parsedProvider]
            : null;
        const scopedReports = selectedReport ? [selectedReport] : chartData.displayReports;

        const creativeChunks = [];
        const publisherChunks = [];
        scopedReports.forEach(([, reportData]) => {
          if (reportData.creativesWithDates) creativeChunks.push(reportData.creativesWithDates);
          if (reportData.publishersWithDates) publisherChunks.push(reportData.publishersWithDates);
        });
        const allCreativesWithDates = [].concat(...creativeChunks);
        const allPublishersWithDates = [].concat(...publisherChunks);
        creativesWithDates = allCreativesWithDates.length > 0 ? allCreativesWithDates : null;
        publishersWithDates = allPublishersWithDates.length > 0 ? allPublishersWithDates : null;
      } else {
        creativesWithDates = chartData.creativesWithDates;
        publishersWithDates = chartData.publishersWithDates;
      }
    }
  } catch (e) {
    // Loud on purpose — anything thrown here leaves creativesWithDates /
    // publishersWithDates undefined, which downgrades both tables to the
    // unfiltered fallback with no other visible symptom.
    console.error('[OverviewTab] failed to read chartData; date filtering disabled', e);
  }

  const filterAndAggregateData = (data, startDate, endDate, dataType) => {
    if (!data || !Array.isArray(data) || !startDate || !endDate) return data;

    let activeCreatives = new Set();
    if (dataType === 'creatives' && chartsBuilt) {
      const fiveDaysBeforeChartsBuilt = moment(chartsBuilt).subtract(5, 'days');
      const chartsBuiltMoment = moment(chartsBuilt);
      data.forEach((item) => {
        if (item.impressions > 0 && item.date) {
          const itemDate = moment(item.date);
          if (itemDate.isSameOrAfter(fiveDaysBeforeChartsBuilt) && itemDate.isSameOrBefore(chartsBuiltMoment)) {
            activeCreatives.add(item.creativeId);
          }
        }
      });
    }

    const filteredData = data.filter((item) => {
      if (!item.date) return true;
      const itemDate = moment(item.date);
      return (
        itemDate.isSameOrAfter(moment(startDate), 'day') &&
        itemDate.isSameOrBefore(moment(endDate), 'day')
      );
    });

    const aggregatedData = {};
    filteredData.forEach((item) => {
      let key, displayName;
      if (dataType === 'creatives') {
        key = item.creativeId || item.creativeName || item.xAxis;
        displayName = item.creativeName || item.creativeId || item.xAxis || `Creative ${key}`;
      } else {
        key = item.publisherName || item.publisherIdentifier || item.xAxis;
        displayName =
          item.publisherName || item.publisherIdentifier || item.xAxis || `Publisher ${key}`;
      }

      if (!aggregatedData[key]) {
        aggregatedData[key] = {
          xAxis: displayName,
          impressions: 0,
          clicks: 0,
          mediaCost: 0,
          totalCost: 0,
          ctr: 0,
          cpc: 0,
          cpm: 0,
          ecpm: 0,
          ...(dataType === 'creatives' && {
            creativeUrl: item.creativeUrl,
            targetUrl: item.targetUrl,
            creativeId: item.creativeId,
            creativeName: item.creativeName,
            s3VideoKey: item.s3VideoKey,
            s3VideoUrl: item.s3VideoUrl,
            activity: activeCreatives.has(item.creativeId) ? 'active' : 'inactive',
          }),
          ...(dataType === 'publishers' && {
            publisherName: item.publisherName,
            publisherIdentifier: item.publisherIdentifier,
            imageUrl: item.imageUrl,
            extra: item.extra,
          }),
        };
      }

      aggregatedData[key].impressions += item.impressions || 0;
      aggregatedData[key].clicks += item.clicks || 0;
      aggregatedData[key].mediaCost += item.mediaCost || 0;
      aggregatedData[key].totalCost += item.totalCost || 0;

      if (
        item.date &&
        (!aggregatedData[key].date || moment(item.date).isAfter(moment(aggregatedData[key].date)))
      ) {
        aggregatedData[key].date = item.date;
      }

      if (aggregatedData[key].impressions > 0) {
        aggregatedData[key].ctr =
          (aggregatedData[key].clicks / aggregatedData[key].impressions) * 100;
      }
    });

    return Object.values(aggregatedData).sort((a, b) => b.impressions - a.impressions);
  };

  const getFilteredCreativeData = () => {
    if (!creativesWithDates || !byCampaignRangeDate || byCampaignRangeDate.length === 0) {
      if (Array.isArray(creatives)) return creatives.map((creative) => ({ ...creative, activity: 'active' }));
      return creatives;
    }
    return filterAndAggregateData(
      creativesWithDates,
      byCampaignRangeDate[0],
      byCampaignRangeDate[1],
      'creatives'
    );
  };

  const getFilteredPublisherData = () => {
    if (!publishersWithDates || !byCampaignRangeDate || byCampaignRangeDate.length === 0) {
      return publishers;
    }
    return filterAndAggregateData(
      publishersWithDates,
      byCampaignRangeDate[0],
      byCampaignRangeDate[1],
      'publishers'
    );
  };

  // Tab-level loading blocker removed. Each inner card (Creatives, Publishers,
  // Segments, LineItemsCard, CampaignsCard, VideoCompletionRate) handles its
  // own loading. Creatives + Publishers render `<EntityCardSkeleton/>` when
  // `chartsLoading` is true; the other 4 sections reuse the same primitive
  // via the `CardSkeleton` helper below (adds the Display-tab mt:10 + z
  // wrapper) so every section on the tab shows the same shimmer vocabulary.
  //
  // Post-load behavior — if a campaign truly has no video logs / no
  // campaigns / no line items, the skeleton briefly shown during load
  // gets replaced by `null`. Minor single-frame flicker, but the
  // alternative (empty void during load) is worse for perceived speed.

  return (
    <div>
      {creatives || !isEmpty(creatives) ? (
        <div style={{ marginTop: 10, position: 'relative', zIndex: 4 }}>
          <Creatives
            colStyle={colStyle}
            selectedCampaign={selectedCampaign}
            creativeData={getFilteredCreativeData()}
            chartsLoading={chartsLoading}
          />
        </div>
      ) : null}
      {hasInsights ? (
        <div style={{ marginTop: 10, position: 'relative', zIndex: 3 }}>
          <Publishers
            colStyle={colStyle}
            selectedCampaign={selectedCampaign}
            publisherData={getFilteredPublisherData()}
            chartsLoading={chartsLoading}
          />
        </div>
      ) : null}
      {segment ? (
        chartsLoading && isEmpty(segments) ? (
          <CardSkeleton zIndex={2} />
        ) : !isEmpty(segments) ? (
          <div style={{ marginTop: 10, position: 'relative', zIndex: 2 }}>
            <SegmentsDetails
              colStyle={colStyle}
              selectedCampaign={selectedCampaign}
              segmentsData={segments}
              selectedSegmentsDimension={selectedSegmentsDimension}
              setSegmentsDimension={setSegmentsDimension}
            />
          </div>
        ) : null
      ) : null}
      {chartsLoading && isEmpty(videoLogs) ? (
        <CardSkeleton />
      ) : videoLogs && !isEmpty(videoLogs) ? (
        <div style={{ marginTop: 10, position: 'relative', zIndex: 1 }}>
          <VideoCompletionRate chartData={videoLogs} />
        </div>
      ) : null}
      {isTrafficker ? (
        chartsLoading && isEmpty(campaigns) ? (
          <CardSkeleton />
        ) : !isEmpty(campaigns) ? (
          <div style={{ marginTop: 10, position: 'relative', zIndex: 1 }}>
            <CampaignsCard data={campaigns} />
          </div>
        ) : null
      ) : null}
      {isTrafficker ? (
        chartsLoading && isEmpty(lineItems) ? (
          <CardSkeleton />
        ) : !isEmpty(lineItems) && lineItems[0].lineItemName ? (
          <div style={{ marginTop: 10, position: 'relative', zIndex: 1 }}>
            <LineItemsCard data={lineItems} />
          </div>
        ) : null
      ) : null}
    </div>
  );
};

export default OverviewTab;
